import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  useGetContactCollectionByFirebaseEventIdOrCreateOneQuery,
  useCreateContactsWithoutTagsMutation,
  useGetTelemetryDataForContactCollectionGuestQuery
} from '@graphql/generated';
import { useTranslation, useRouterHelper } from '@shared/core';
import { useContactCollectorGuestTelemetryTelemetry } from '../guest/ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '../guest';
import { NATIONAL_COUNTRY, NATIONAL_COUNTRY_CODE } from '@apps/admin/common/ShippingAddress/constants';
import { useToast } from '@withjoy/joykit';

export interface ContactInputFields
  extends Readonly<{
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    email?: string;
    address1?: string;
    address2?: string;
    postalCode?: string;
    city?: string;
    region?: string;
    country?: string;
    countryCode?: string;
    validated: boolean;
  }> {}

export type Contacts = { contacts: ContactInputFields[] };

export const contactInitialValue: ContactInputFields = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  region: '',
  country: NATIONAL_COUNTRY,
  countryCode: NATIONAL_COUNTRY_CODE,
  validated: false
};

export const initialValues: Contacts = {
  contacts: [contactInitialValue]
};

const yupSchema = (tRequired: string, tInvalidEmail: string) =>
  Yup.object({
    contacts: Yup.array()
      .min(1)
      .of(
        Yup.object<ContactInputFields>({
          firstName: Yup.string().test('first-element-first-name-required', tRequired, function (this, value) {
            if (this.path === 'contacts[0].firstName' && !value) {
              return false;
            }
            return true;
          }),
          lastName: Yup.string().test('first-element-last-name-required', tRequired, function (this, value) {
            if (this.path === 'contacts[0].lastName' && !value) {
              return false;
            }
            return true;
          }),
          phoneNumber: Yup.string(),
          email: Yup.string().email(tInvalidEmail),
          address1: Yup.string(),
          address2: Yup.string(),
          postalCode: Yup.string(),
          city: Yup.string(),
          region: Yup.string(),
          country: Yup.string(),
          countryCode: Yup.string(),
          validated: Yup.boolean()
        })
      )
  });

export const useContactCollectorIframeController = (firebaseEventId: string) => {
  const [e164PhoneNumber, setE164PhoneNumber] = useState<{ [key: number]: string }>({ 0: '' });
  const { trackError } = useContactCollectorGuestTelemetryTelemetry();
  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');
  const { toast } = useToast();

  const { data, loading, error } = useGetContactCollectionByFirebaseEventIdOrCreateOneQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      firebaseEventId
    },
    onError: error => trackError('Query', error)
  });

  const eventId = data?.getContactCollectionByFirebaseEventIdOrCreateOne?.eventId;

  const { data: telemetryQueryData, loading: isTelemetryQueryLoading } = useGetTelemetryDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: eventId!
    },
    skip: !eventId,
    onError: error => trackError('Query', error)
  });

  const [createContactsMutation, { loading: isCreateContactsMutationLoading, error: createContactsMutationError }] = useCreateContactsWithoutTagsMutation();

  const formik = useFormik<Contacts>({
    initialValues,
    validationSchema: yupSchema(tAdmin.required(), tAdmin.invalidEmail()),
    validateOnMount: true,
    onSubmit: async (values, formikHelpers) => {
      try {
        const contactCollectionId = data?.getContactCollectionByFirebaseEventIdOrCreateOne?.id;

        if (!contactCollectionId) {
          throw Error('No Collection Id provided');
        }

        const mutationData = values.contacts.map((contact, index) => ({
          address:
            // we do not want to save addresses from household members which are not the head of the household
            // also since address is optional we are not saving partial address, we save it only when it's complete
            index === 0 && contact.address1 && contact.city && contact.country && contact.countryCode && contact.postalCode && contact.region
              ? {
                  address1: contact.address1,
                  address2: contact.address2,
                  city: contact.city,
                  country: contact.country,
                  countryCode: contact.countryCode,
                  postalCode: contact.postalCode,
                  region: contact.region,
                  validated: contact.validated
                }
              : undefined,
          contact: {
            contactCollectionId,
            firebaseEventId,
            eventId,
            email: contact.email ? contact.email.trim() : undefined,
            firstName: contact.firstName.trim(),
            lastName: contact.lastName.trim(),
            party: values.contacts.length > 1 ? `${contactCollectionId}-${values.contacts[0].firstName}-${values.contacts[0].lastName}` : undefined
          },
          phoneNumbers: contact.phoneNumber
            ? [
                {
                  allowSMS: true,
                  mobile: true,
                  number: contact.phoneNumber.trim()
                }
              ]
            : undefined
        }));
        await createContactsMutation({ variables: { data: mutationData } });
        setE164PhoneNumber({ 0: '' });
        formikHelpers.resetForm();
        routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.form.path);
        toast(`${tAdmin.contact()}${values.contacts.length > 1 ? 's' : ''} ${tAdmin.created()}`, { position: 'bottom-center' });
      } catch (error) {
        trackError('Mutation', error);
      }
    }
  });

  return {
    tAdmin,
    showErrorPage: !!error,
    isLoading: loading || isTelemetryQueryLoading,
    formik,
    isMutationLoading: isCreateContactsMutationLoading,
    hasMutationError: !!createContactsMutationError,
    telemetryData: {
      postgresEventId: telemetryQueryData?.eventById?.id ?? '',
      firebaseEventId: telemetryQueryData?.eventById?.firebaseId ?? '',
      joyEventType: telemetryQueryData?.eventById?.info.eventType ?? ''
    },
    e164PhoneNumber,
    setE164PhoneNumber
  };
};
